import { registerMockAPI } from '~/mocks/mockApis';

import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { axiosInstance } from '~/services';

import { PICKUP_SHIPPING_TYPES } from '~/constants';

import { receiveKeys } from './keys';

import type {
  TypeApiResponse,
  TypeGetRequestsRequestedResponse,
  TypeReceiveDetailOrderItem,
} from '~/types/receive.types';

type RequestsRequestedParams = {
  ownerCode: string;
  requestId: string;
  enabled?: boolean;
};

/** 변환된 응답 타입 */
type FormattedResponse = Omit<TypeGetRequestsRequestedResponse, 'orderItems'> & {
  orderItems: TypeReceiveDetailOrderItem[];
};

/**
 * [입고요청관리] 입고요청 상세 조회 API
 */
const fetchGetRequestsRequested = async (ownerCode: string, requestId: string) => {
  const endpoint = '/api-inbound/v1/3pl/receiving';
  const { data } = await axiosInstance.get<TypeApiResponse<TypeGetRequestsRequestedResponse>>(
    `${endpoint}/${ownerCode}/requests/requested/${requestId}`
  );
  return data.data;
};

/**
 * orderItems를 TypeReceiveDetailOrderItem 형태로 변환
 */
const transformResponse = (data: TypeGetRequestsRequestedResponse): FormattedResponse => {
  const formattedItems: TypeReceiveDetailOrderItem[] = data.orderItems.map((item, index) => {
    const isUsingDate = !!(item.expirationDate ?? item.manufactureDate);
    const dateType = item.expirationDate ? 'expirationDate' : 'manufactureDate';
    const date = item.expirationDate ?? item.manufactureDate;

    return {
      index: index + 1,
      itemId: item.itemId,
      goodsId: item.goodsId,
      goodsCode: item.goodsCode,
      nsid: item.nsid,
      alternativeCode: item.alternativeCode,
      goodsName: item.goodsName,
      kurlyKeepTypeDesc: item.kurlyKeepTypeDesc,
      dateUseYn: isUsingDate,
      dateType: isUsingDate ? dateType : null,
      date: isUsingDate ? date : null,
      usePallet: item.usePallet,
      lotNo: item.lotNo ?? '',
      boxQty: item.boxQty,
      boxPerQty: item.boxPerQty,
      eachQty: item.eachQty,
      totalQty: item.totalQty,
    };
  });

  return { ...data, orderItems: formattedItems };
};

/**
 * [입고요청관리] 입고요청 상세 조회 Query Hook
 * @returns 변환된 응답 타입 (FormattedResponse)
 */
export const useGetRequestsRequestedQuery = ({ ownerCode, requestId, enabled = true }: RequestsRequestedParams) => {
  return useQuery({
    queryKey: receiveKeys.requestsRequestedDetail(ownerCode, requestId),
    queryFn: () => fetchGetRequestsRequested(ownerCode, requestId),
    select: transformResponse,
    enabled,
    staleTime: 0,
    gcTime: 0,
  });
};

const baseOrderItems: TypeGetRequestsRequestedResponse['orderItems'] = [
  {
    itemId: 48658,
    goodsId: 171759,
    goodsCode: 'M00000107605',
    nsid: 'nsid',
    alternativeCode: '8812312312012',
    goodsName: '(센터명)화주사상품',
    mainCategoryCode: 'FI_00000033',
    keepLevel: '1',
    kurlyKeepTypeDesc: '냉장',
    shelfLifeType: 'USE_DATE',
    expirationDate: '2027-09-06',
    manufactureDate: null,
    usePallet: false,
    boxQty: 10,
    boxPerQty: 10,
    eachQty: 0,
    totalQty: 100,
    lotNo: 'LOT111111111',
  },
  {
    itemId: 48659,
    goodsId: 171761,
    goodsCode: 'M00000107606',
    nsid: 'nsid',
    alternativeCode: '8812312312013',
    goodsName: '(센터명)화주사상품2',
    mainCategoryCode: 'FI_00000033',
    keepLevel: '1',
    kurlyKeepTypeDesc: '냉장',
    shelfLifeType: 'USE_DATE',
    expirationDate: null,
    manufactureDate: null,
    usePallet: false,
    boxQty: 10,
    boxPerQty: 10,
    eachQty: 0,
    totalQty: 100,
    lotNo: 'LOT111111111',
  },
];

const basePickupInfos = {
  defaultPickupReleaseInfos: [
    {
      id: 1,
      palletQty: '1',
      vehicleSizeCode: 'ONE_TON',
      memo: '컬리 픽업 테스트',
      zipNo: '12345',
      address: '파포에서 받아온 기본 출고지 주소',
      detailAddress: '파포에서 받아온 기본 출고지 상세주소',
      phoneNo: '010-1234-5678',
      defaultOutboundYn: true,
    },
  ],
  extraPickupReleaseInfos: [
    {
      id: 2,
      palletQty: '1',
      vehicleSizeCode: 'THREE_HALF_TON',
      memo: '변경 메모',
      zipNo: '12345',
      address: '기타 출고지 주소',
      detailAddress: '기타 출고지 상세주소',
      phoneNo: '010-1234-5678',
      defaultOutboundYn: false,
    },
  ],
};

/**
 * 입고요청 상세 응답 빌더.
 * `receivingExpectedDate`는 today+3으로 동적 생성 — cutoff(D-1 17시) 이전이라 픽업 수정 흐름 검증 가능.
 */
const buildDetailResponse = (
  overrides: Partial<TypeGetRequestsRequestedResponse> = {}
): TypeApiResponse<TypeGetRequestsRequestedResponse> => ({
  data: {
    ownerName: '3PL 수신 테스트',
    orderId: 11423,
    inboundAreaAddress: '센소',
    releaseCode: PICKUP_SHIPPING_TYPES.KURLY_PICKUP,
    releaseName: '컬리픽업',
    courierName: null,
    invoiceNo: null,
    receivingExpectedDate: dayjs().add(3, 'day').format('YYYY-MM-DD'),
    dailyCapaId: 10845,
    cluster: 'CC03',
    warehouse: 'GPM1',
    xdock: true,
    xdockCenter: 'GPH1',
    supplierId: 2,
    supplierCode: 'SU000013',
    supplierName: '테스트공급사',
    remarks: null,
    status: 'REQUESTED',
    cancelReason: null,
    cancelUserId: null,
    orderItems: baseOrderItems,
    ...basePickupInfos,
    ...overrides,
  },
});

registerMockAPI<TypeApiResponse<TypeGetRequestsRequestedResponse>>({
  method: 'get',
  endpoint: '/api-inbound/v1/3pl/receiving/:ownerCode/requests/requested/:requestId',
  label: '입고/입고요청/상세',
  scenarios: [
    {
      name: '정상 응답 — 컬리픽업+xdock 수정 가능',
      response: () => buildDetailResponse(),
    },
    {
      name: '정상 응답 — 확인완료',
      response: () => buildDetailResponse({ status: 'CONFIRMED' }),
    },
    {
      name: '정상 응답 — 요청취소',
      response: () =>
        buildDetailResponse({
          status: 'CANCELED',
          cancelUserId: 'test_user',
          cancelReason: '테스트 취소 사유',
        }),
    },
    {
      name: '정상 응답 — 택배',
      response: () =>
        buildDetailResponse({
          releaseCode: 'PARCEL',
          releaseName: '택배',
          courierName: 'CJ대한통운',
          invoiceNo: '1234567890',
          xdock: false,
          xdockCenter: null,
        }),
    },
    {
      name: '요청 미존재 (9011)',
      response: {
        error: {
          code: 9011,
          message: '요청을 찾을 수 없습니다.',
        },
      },
      status: 400,
    },
  ],
});
